


import { Component, Vue, Ref } from 'vue-property-decorator';
import ContactForm from './components/ContactForm/index.vue';
import SubmitArea from './components/SubmitArea/index.vue';
import ChannelSelection from './components/ChannelSelection/index.vue';
import ImageCollage from './components/ImageCollage/index.vue';
import { namespace } from 'vuex-class';
import { ContactFormFields, ContactType } from './components/ContactForm/ContactForm.models';
import PageTransitionMode from '../../utils/mixins/pageTransitionMode';
import { Route } from 'vue-router';
import { tRoute } from '../../utils/plugins/i18nRoutes';
import { RouteNames } from '../../utils/router/router.models';

const contactModule = namespace('contact');
const notifyModule = namespace('notify');
const appModule = namespace('app');

Component.registerHooks(['beforeRouteEnter']);

@Component({
  mixins: [PageTransitionMode],
  components: {
    ContactForm,
    SubmitArea,
    ChannelSelection,
    ImageCollage
  }
})
export default class Contact extends Vue {
  @contactModule.State private fields!: ContactFormFields;
  @contactModule.State private privacyApproved!: boolean;

  @appModule.Getter private isTablet!: boolean;

  @contactModule.Action private updateFieldValue!: (data: { field: keyof ContactFormFields; value: string }) => void;
  @contactModule.Action private updatePrivacyValue!: (value: boolean) => void;
  @contactModule.Action private submitForm!: (type: ContactType) => void;
  @contactModule.Action private clearFieldError!: (field: keyof ContactFormFields) => void;
  @contactModule.Action private resetState!: () => void;
  @contactModule.Action private closeValidationNotification!: () => void;
  @contactModule.Action private closePrivacyNotification!: () => void;
  @notifyModule.Action private closeNotificationsOfOrigin!: (origin: string) => void;

  @Ref('contactForm') private contactForm!: Vue;
  @Ref('submitArea') private submitArea!: HTMLElement;

  public fromRoute!: Route;

  private handleInput(data: { value: string; field: keyof ContactFormFields }) {
    this.updateFieldValue(data);
  }

  private handleFocus(field: keyof ContactFormFields) {
    this.clearFieldError(field);
    this.closeValidationNotification();
  }

  private privacyInput(value: boolean) {
    this.updatePrivacyValue(value);
    this.closePrivacyNotification();
  }

  private handleSubmit() {
    this.submitForm(this.$route.meta?.contactType as ContactType);
  }

  private beforeRouteLeave(_to: Route, _from: Route, next: () => void) {
    this.resetState();
    this.closeNotificationsOfOrigin('contact');
    next();
  }

  private beforeRouteEnter(_to: Route, from: Route, next: (vw: unknown) => void) {
    next((vm: { fromRoute: Route } & Vue) => {
      vm.fromRoute = from;
    });
  }

  private get scrollContainer(): HTMLDivElement {
    return this.isTablet ? (this.$el as HTMLDivElement) : (this.contactForm.$el as HTMLDivElement);
  }

  private contactFormTextarea(): HTMLInputElement {
    // TODO: consider retrieving this element via querySelector instead
    return (this.contactForm.$refs['formFields'] as Vue[]).find(
      (field: Vue) => (field?.$options?.propsData as { field: { type: string } })?.field?.type === 'textarea'
    )?.$el as HTMLInputElement;
  }

  private async scrollToBottom() {
    await this.$nextTick();

    this.scrollContainer.scrollTo({
      top: this.contactFormTextarea().offsetTop - 70,
      behavior: 'smooth'
    });
  }

  private scrollToTop() {
    this.scrollContainer.scrollTo({
      top: this.contactFormTextarea().offsetTop - this.scrollContainer.offsetHeight, // + this.submitArea.clientHeight,
      behavior: 'smooth'
    });
  }

  private navigateBackToSummary() {
    if (
      [
        tRoute(RouteNames.SummaryFloorGroundFloor).name,
        tRoute(RouteNames.SummaryFloorUpperFloor).name,
        tRoute(RouteNames.SummaryFloorAttic).name
      ].includes(this.fromRoute?.name || '')
    ) {
      this.$router.push(this.fromRoute.name ? { name: this.fromRoute.name } : tRoute(RouteNames.Summary));
    } else {
      this.$router.push(tRoute(RouteNames.Summary));
    }
  }
}
